import { useEffect, useRef } from 'react';
import { useSyncExternalStore } from 'react';
import {
  anyClipRendering,
  getLastRenderEventAt,
  subscribeRenderStore,
} from '../components/clips/renderState';

/** How often to refetch the clips list while a render is in flight. */
export const RENDER_POLL_INTERVAL_MS = 5000;
/**
 * If a genuine WS RenderProgress event landed within this window, live delivery
 * is working — skip the poll tick and let the WS stream drive the bar.
 */
export const RENDER_POLL_WS_QUIET_MS = 6000;

/**
 * Refetch fallback for the clip render progress bar. While at least one tracked
 * clip is "rendering", it refetches the clips list on an interval so the
 * forward-progress seeds (reconcileSeed) advance the bar even when the live WS
 * progress stream is being dropped in transport. It:
 *   - only runs while something is rendering (interval torn down otherwise),
 *   - skips a tick if a refetch is already in flight (no overlap),
 *   - skips a tick if a real WS event arrived very recently.
 *
 * Mount once where the clips list refresh callback lives (ClipPromptsList), not
 * per clip card.
 */
export function useRenderPollingFallback(onRefetch: () => void | Promise<void>): void {
  const anyRendering = useSyncExternalStore(subscribeRenderStore, anyClipRendering);
  const inFlightRef = useRef(false);
  // Keep the latest callback without restarting the interval each render.
  const onRefetchRef = useRef(onRefetch);
  onRefetchRef.current = onRefetch;

  useEffect(() => {
    if (!anyRendering) return;

    const tick = async () => {
      if (inFlightRef.current) return;
      if (Date.now() - getLastRenderEventAt() < RENDER_POLL_WS_QUIET_MS) return;
      inFlightRef.current = true;
      try {
        await onRefetchRef.current();
      } catch {
        // Swallow: the next tick retries, and the list component surfaces its
        // own fetch error state.
      } finally {
        inFlightRef.current = false;
      }
    };

    const id = window.setInterval(tick, RENDER_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [anyRendering]);
}
